import { useCallback, useMemo, useState } from "react";

export const PARTNER_WIZARD_STEPS = [
  "user",
  "product",
  "checkout",
  "status",
  "comments",
  "summary",
];

export function isPartnerStepValid(step, state) {
  const { userData, productData, pendingComment } = state;

  switch (step) {
    case "user":
      return Boolean(userData?._id);
    case "product": {
      const { gradingType, numberOfCards, priority, declaredValue } = productData;
      return (
        Boolean(gradingType) &&
        Boolean(numberOfCards) &&
        numberOfCards !== "0" &&
        Boolean(priority) &&
        Number(declaredValue) >= 100
      );
    }
    case "checkout":
      return Boolean(productData?.finalPrice) && Boolean(productData?.deadline);
    case "status":
      return Boolean(productData?.status);
    case "comments":
      return !pendingComment;
    case "summary":
      return true;
    default:
      return false;
  }
}

export function useCreatePartnerProductWizard({
  initialUserData,
  initialStep,
  storeId,
} = {}) {
  const [step, setStep] = useState(
    initialStep || (initialUserData?._id ? "product" : "user")
  );
  const [userData, setUserData] = useState(initialUserData || {});
  const [deliveryData, setDeliveryData] = useState({
    delivery: "presencial",
    store: storeId || "",
  });
  const [productData, setProductData] = useState({
    status: "GENERATED",
    comments: [],
  });
  const [pendingComment, setPendingComment] = useState("");

  const stepOrder = PARTNER_WIZARD_STEPS;
  const stepIndex = stepOrder.indexOf(step);

  const validationState = useMemo(
    () => ({ userData, productData, pendingComment }),
    [userData, productData, pendingComment]
  );

  const canNext = isPartnerStepValid(step, validationState);
  const canPrev = stepIndex > 0;

  const next = useCallback(() => {
    if (!isPartnerStepValid(step, validationState)) return;
    const nextStep = stepOrder[stepIndex + 1];
    if (nextStep) setStep(nextStep);
  }, [step, stepIndex, stepOrder, validationState]);

  const prev = useCallback(() => {
    const prevStep = stepOrder[stepIndex - 1];
    if (prevStep) setStep(prevStep);
  }, [stepIndex, stepOrder]);

  const goToStep = useCallback(
    (target) => {
      if (stepOrder.includes(target)) setStep(target);
    },
    [stepOrder]
  );

  return {
    step,
    stepIndex,
    stepOrder,
    userData,
    setUserData,
    deliveryData,
    setDeliveryData,
    productData,
    setProductData,
    pendingComment,
    setPendingComment,
    canNext,
    canPrev,
    next,
    prev,
    goToStep,
  };
}
